/**
 * Timestamp-based SortKey Utilities for Alternative 5
 * Format: "2024-01-15T10:00:00.500Z#0001"
 * 
 * This approach eliminates O(N) reindexing by generating unique sortKeys
 * that maintain order without requiring updates to other cars.
 * 
 * If two cars have identical timestamps and consecutive sequences, the format
 * can append digits to the sequence (e.g., #00015) to maintain order
 * lexicographically without reindexing.
 */

/**
 * Compare two sortKeys for sorting
 * 
 * @param {string|number} a - First sortKey
 * @param {string|number} b - Second sortKey
 * @returns {number} -1 if a < b, 0 if a === b, 1 if a > b
 */
export const compareSortKeys = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return -1; // Falsy values sort BEFORE valid keys
  if (!b) return 1;  // Valid keys sort AFTER falsy values
  
  // Handle legacy integer sortKeys during migration
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  if (typeof a === 'number') return -1; // Numbers come before strings
  if (typeof b === 'number') return 1;
  
  // Lexicographic comparison works for ISO timestamps
  return a.localeCompare(b);
};

/**
 * Convert legacy integer sortKey to timestamp format
 * Uses a base timestamp (2020-01-01) and adds the integer as days
 * This preserves relative order while converting to new format
 * 
 * @param {number|string} legacySortKey - Legacy integer sortKey
 * @returns {string} Timestamp format sortKey
 */
const convertLegacySortKey = (legacySortKey) => {
  const baseDate = new Date('2020-01-01T00:00:00.000Z');
  const intValue = typeof legacySortKey === 'number' ? legacySortKey : parseInt(legacySortKey, 10);
  if (isNaN(intValue)) {
    console.error(`Invalid legacy sortKey: ${legacySortKey}`);
    return `${new Date().toISOString()}#0001`;
  }
  // Add integer as days (86400000 ms per day) to preserve order
  const convertedTime = baseDate.getTime() + (intValue * 86400000);
  return `${new Date(convertedTime).toISOString()}#0001`;
};

/**
 * Generate timestamp-based sortKey between two existing sortKeys
 * 
 * @param {string|number|null} prevSortKey - Previous car's sortKey (null for beginning)
 * @param {string|number|null} nextSortKey - Next car's sortKey (null for end)
 * @param {Array<TrackCar>} existingCars - All cars on track (for collision detection)
 * @returns {string} New sortKey in format "timestamp#sequence"
 */
export const generateTimestampSortKey = (prevSortKey, nextSortKey, existingCars = []) => {
  let timestamp;
  let sequence = '0001';
  
  // Convert legacy integer sortKeys to timestamp format
  if (prevSortKey !== null && prevSortKey !== undefined) {
    if (typeof prevSortKey === 'number' || (typeof prevSortKey === 'string' && /^\d+$/.test(prevSortKey))) {
      prevSortKey = convertLegacySortKey(prevSortKey);
    }
  }
  if (nextSortKey !== null && nextSortKey !== undefined) {
    if (typeof nextSortKey === 'number' || (typeof nextSortKey === 'string' && /^\d+$/.test(nextSortKey))) {
      nextSortKey = convertLegacySortKey(nextSortKey);
    }
  }
  
  // Helper: Parse sortKey into components
  const parseSortKey = (sortKey) => {
    // Handle null/undefined
    if (!sortKey) return null;
    
    // Should not happen after conversion, but handle as safety
    if (typeof sortKey === 'number') {
      return null; // Should have been converted above
    }
    
    if (typeof sortKey !== 'string') return null;
    
    // Check if string is legacy integer format (all digits) - should have been converted
    if (/^\d+$/.test(sortKey)) {
      return null; // Should have been converted above
    }
    
    const parts = sortKey.split('#');
    if (parts.length !== 2) return null; // Invalid format
    
    const timestampStr = parts[0];
    const time = new Date(timestampStr).getTime();
    
    // Validate timestamp is valid
    if (isNaN(time)) return null; // Invalid timestamp
    
    return {
      timestamp: timestampStr,
      sequence: parseInt(parts[1] || '0', 10),
      sequenceStr: parts[1] || '0000',
      time: time
    };
  };
  
  const prev = parseSortKey(prevSortKey);
  const next = parseSortKey(nextSortKey);
  
  // Case 1: Insert at beginning (before first car)
  if (!prev && next) {
    // Subtract 1 second, but ensure we don't go below 0
    const beforeTime = Math.max(0, next.time - 1000);
    timestamp = new Date(beforeTime).toISOString();
    return `${timestamp}#${sequence}`;
  }
  
  // Case 2: Insert at end (after last car)
  if (prev && !next) {
    // Add 1 second, check for Date overflow
    const afterTime = prev.time + 1000;
    // Date.MAX_VALUE is 8640000000000000, so adding 1000 is safe
    timestamp = new Date(afterTime).toISOString();
    return `${timestamp}#${sequence}`;
  }
  
  // Case 3: Insert between two cars
  if (prev && next) {
    // Special case: If timestamps are identical, use same timestamp with sequence between prev and next
    // Sequence numbers are the tie-breaker when timestamps match (first car gets #0001, next gets #0002, etc.)
    if (prev.time === next.time) {
      timestamp = prev.timestamp; // Use the same timestamp as prev/next
      
      // Find sequence numbers of prev and next
      const prevSeq = prev.sequence;
      const nextSeq = next.sequence;
      
      // Find all cars with same timestamp to check for gaps in sequence numbers
      const sameTimeCars = existingCars.filter(car => {
        let carSortKey = car.sortKey ?? '';
        // Convert legacy integer sortKeys for comparison
        if (typeof carSortKey === 'number' || (typeof carSortKey === 'string' && /^\d+$/.test(carSortKey))) {
          carSortKey = convertLegacySortKey(carSortKey);
        }
        return carSortKey && typeof carSortKey === 'string' && carSortKey.startsWith(timestamp);
      });
      
      // Extract all sequence numbers at this timestamp into a Set for fast lookup
      const existingSeqs = new Set(sameTimeCars.map(car => {
        let carSortKey = car.sortKey ?? '';
        // Convert legacy integer sortKeys for parsing
        if (typeof carSortKey === 'number' || (typeof carSortKey === 'string' && /^\d+$/.test(carSortKey))) {
          carSortKey = convertLegacySortKey(carSortKey);
        }
        const parts = carSortKey.split('#');
        return parseInt(parts[1] || '0', 10);
      }));
      
      // Try to find a sequence number between prevSeq and nextSeq
      // Sequence numbers are the tie-breaker: first car gets #0001, next gets #0002, etc.
      let targetSeq;
      if (nextSeq > prevSeq + 1) {
        // There's a gap between prev and next: try to use a number in that gap
        // Start with midpoint, but find first available if midpoint is taken
        targetSeq = Math.floor((prevSeq + nextSeq) / 2);
        
        // If midpoint is taken, find first available sequence in the gap
        if (existingSeqs.has(targetSeq)) {
          for (let seq = prevSeq + 1; seq < nextSeq; seq++) {
            if (!existingSeqs.has(seq)) {
              targetSeq = seq;
              break;
            }
          }
          // If all sequences in gap are taken, use the suffix trick to stay between
          if (targetSeq >= nextSeq) {
            return `${timestamp}#${prev.sequenceStr}5`;
          }
        }
      } else {
        // prevSeq and nextSeq are consecutive: can't insert between them at same timestamp
        // Instead of using maxSeq + 1 (which would sort after next), we use a lexicographical
        // midpoint by appending a digit to the previous sequence. This ensures the new key
        // sorts between prev and next: "T#0001" < "T#00015" < "T#0002"
        const prevSeqStr = prev.sequenceStr || prev.sequence.toString().padStart(4, '0');
        return `${timestamp}#${prevSeqStr}5`;
      }
      
      // Handle sequence overflow
      if (targetSeq > 9999) {
        // Sequence overflow: use next millisecond with reset sequence
        const nextTime = prev.time + 1;
        timestamp = new Date(nextTime).toISOString();
        sequence = '0001';
        return `${timestamp}#${sequence}`;
      }
      
      sequence = targetSeq.toString().padStart(4, '0');
      return `${timestamp}#${sequence}`;
    }
    
    // Normal case: Different timestamps
    // Calculate midpoint timestamp
    let midTime = Math.floor((prev.time + next.time) / 2);
    
    // Ensure minimum 1ms gap from prev
    if (midTime <= prev.time) {
      midTime = prev.time + 1;
    }
    // Ensure minimum 1ms gap from next
    if (midTime >= next.time) {
      // No space between consecutive timestamps - use prev.time with sequence increment
      midTime = prev.time;
    }
    
    timestamp = new Date(midTime).toISOString();
    
    // Check for collisions with existing cars at same timestamp
    // Convert legacy sortKeys for comparison
    const sameTimeCars = existingCars.filter(car => {
      let carSortKey = car.sortKey ?? '';
      // Convert legacy integer sortKeys for comparison
      if (typeof carSortKey === 'number' || (typeof carSortKey === 'string' && /^\d+$/.test(carSortKey))) {
        carSortKey = convertLegacySortKey(carSortKey);
      }
      return carSortKey && typeof carSortKey === 'string' && carSortKey.startsWith(timestamp);
    });
    
    if (sameTimeCars.length > 0) {
      // Find highest sequence number for this timestamp
      const maxSeq = sameTimeCars.reduce((max, car) => {
        let carSortKey = car.sortKey ?? '';
        // Convert legacy integer sortKeys for parsing
        if (typeof carSortKey === 'number' || (typeof carSortKey === 'string' && /^\d+$/.test(carSortKey))) {
          carSortKey = convertLegacySortKey(carSortKey);
        }
        const parts = carSortKey.split('#');
        const seq = parseInt(parts[1] || '0', 10);
        return Math.max(max, seq);
      }, 0);
      
      // Increment sequence with overflow handling
      const nextSeq = maxSeq + 1;
      if (nextSeq > 9999) {
        // Sequence overflow: use next millisecond with reset sequence
        midTime = midTime + 1;
        timestamp = new Date(midTime).toISOString();
        sequence = '0001';
      } else {
        sequence = nextSeq.toString().padStart(4, '0');
      }
    }
    
    return `${timestamp}#${sequence}`;
  }
  
  // Case 4: First car on track
  timestamp = new Date().toISOString();
  return `${timestamp}#${sequence}`;
};

/**
 * Calculate sortKey for inserting at specific position
 * 
 * @param {number} targetPosition - Desired position (0-based index)
 * @param {Array<TrackCar>} existingCars - Current cars on track (sorted by sortKey)
 * @param {boolean} isPreSorted - Optional flag indicating if existingCars is already sorted
 * @returns {string} New sortKey
 */
export const getSortKeyForPosition = (targetPosition, existingCars, isPreSorted = false) => {
  // Input validation
  if (!Array.isArray(existingCars)) {
    // Invalid input: return current timestamp as fallback
    const timestamp = new Date().toISOString();
    return `${timestamp}#0001`;
  }
  
  if (typeof targetPosition !== 'number' || isNaN(targetPosition)) {
    targetPosition = 0;
  }
  
  // Validate targetPosition bounds
  if (targetPosition < 0) targetPosition = 0;
  if (targetPosition > existingCars.length) targetPosition = existingCars.length;
  
  // Sort cars by sortKey (string comparison) - only if not already sorted
  const sorted = isPreSorted 
    ? existingCars 
    : [...existingCars].sort((a, b) => {
        const aKey = a?.sortKey ?? '';
        const bKey = b?.sortKey ?? '';
        return compareSortKeys(aKey, bKey);
      });
  
  let prevSortKey = null;
  let nextSortKey = null;
  
  if (targetPosition === 0) {
    // Insert at beginning
    const rawNextSortKey = sorted[0]?.sortKey ?? null;
    nextSortKey = rawNextSortKey;
  } else if (targetPosition >= sorted.length) {
    // Insert at end
    const rawPrevSortKey = sorted[sorted.length - 1]?.sortKey ?? null;
    prevSortKey = rawPrevSortKey;
  } else {
    // Insert between
    const prevCar = sorted[targetPosition - 1];
    const nextCar = sorted[targetPosition];
    prevSortKey = prevCar?.sortKey ?? null;
    nextSortKey = nextCar?.sortKey ?? null;
  }
  
  return generateTimestampSortKey(prevSortKey, nextSortKey, sorted);
};

/**
 * Extract sequence from sortKey
 * 
 * @param {string} sortKey - SortKey in format "timestamp#sequence"
 * @returns {number} Sequence number
 */
export const extractSequence = (sortKey) => {
  if (!sortKey || typeof sortKey !== 'string') return 0;
  const parts = sortKey.split('#');
  const seqStr = parts[1] || '0';
  const integerPart = seqStr.slice(0, 4);
  const fractionalPart = seqStr.slice(4);
  return parseFloat(integerPart + (fractionalPart ? '.' + fractionalPart : '')) || 0;
};

/**
 * Validate sortKey format
 * 
 * @param {string|number} sortKey - SortKey to validate
 * @returns {boolean} True if valid
 */
export const isValidSortKey = (sortKey) => {
  if (typeof sortKey === 'number') return true; // Legacy integer format
  if (typeof sortKey !== 'string') return false;
  const parts = sortKey.split('#');
  if (parts.length !== 2) return false;
  const timestamp = parts[0];
  const sequence = parts[1];
  return !isNaN(new Date(timestamp).getTime()) && /^\d{4,}$/.test(sequence);
};

